import {
  Space,
  Typography,
  Row,
  Col,
  Table,
  Button,
  Input,
  Modal,
  Form,
  Input as AntInput,
} from "antd";
import React, { useState, useRef, useEffect } from "react";
import LineChart from "../Chart.js/LineChart";
import { EyeOutlined, UserAddOutlined } from "@ant-design/icons";
import "./UserList.css";

function UserList() {
  const [userList, setUserList] = useState([]);
  const lineData = [
    { month: "Jan", users: 500 },
    { month: "Feb", users: 800 },
    { month: "Mar", users: 600 },
    { month: "Apr", users: 1000 },
    { month: "May", users: 1200 },
  ];

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false); // Modal for adding users
  const [isViewModalOpen, setIsViewModalOpen] = useState(false); // Modal for viewing users
  const [viewedUser, setViewedUser] = useState(null);
  const [form] = Form.useForm();
  const searchInput = useRef(null);

  const [userCountsByMonth, setUserCountsByMonth] = useState([]);
  const [averageTimeByMonth, setAverageTimeByMonth] = useState([]);
  const [trafficData, setTrafficData] = useState([]);

  const fetchUserList = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/getAllUsers`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setUserList(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error("Error fetching user list:", error);
    }
  };

  const fetchAvarageTime = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/session/average-time`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      const last12Months = [];
      for (let i = 0; i < 12; i++) {
        let year = currentYear;
        let month = currentMonth - (11 - i);

        if (month < 0) {
          month = 12 + month;
          year = currentYear - 1;
        }

        last12Months.push(year + "-" + String(month + 1).padStart(2, "0")); // "YYYY-MM"
      }

      // Transform the data
      const transformedData = last12Months.map((monthYear) => {
        const found = data.find((item) => item.month === monthYear);
        return {
          month: new Date(monthYear + "-01").toLocaleDateString("en-US", {
            month: "short",
          }),
          averageTime: found ? found.averageTime : 0,
        };
      });

      setAverageTimeByMonth(transformedData);
    } catch (error) {
      console.error("Error fetching average time:", error);
    }
  };
  const calculateUserCountsByMonth = (users) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const counts = [];

    for (let i = 0; i < 12; i++) {
      // Changed loop direction
      let year = currentYear;
      let month = currentMonth - (11 - i); // Changed subtraction order

      if (month < 0) {
        month = 12 + month;
        year = currentYear - 1;
      }

      const monthName = monthNames[month];

      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0); // Last day of the month

      const userCount = users.filter((user) => {
        const userCreatedAt = new Date(user.createdAt);
        return userCreatedAt >= startDate && userCreatedAt <= endDate;
      }).length;

      counts.push({ month: monthName, users: userCount });
    }

    return counts;
  };
  const fetchTrafficData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/session/count-sessions`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();

      const now = new Date();

      // Tạo mảng 12 tháng gần nhất với tên viết tắt của tháng
      const last12Months = [];
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const period = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;
        const monthLabel = date.toLocaleString("default", { month: "short" });

        last12Months.push({ period, monthLabel });
      }

      const trafficData = last12Months.map(({ period, monthLabel }) => {
        const match = result.data.find((item) => item.period === period);
        return {
          month: monthLabel,
          traffic: match ? match.totalSessions : 0,
        };
      });

      setTrafficData(trafficData);
    } catch (error) {
      console.error("Error fetching traffic data:", error);
      setTrafficData([]);
    }
  };

  useEffect(() => {
    if (userList.length > 0) {
      const monthlyCounts = calculateUserCountsByMonth(userList);
      setUserCountsByMonth(monthlyCounts);
    }
  }, [userList]);

  useEffect(() => {
    fetchUserList();
    fetchAvarageTime();
    fetchTrafficData();
  }, []);

  const columns = [
    {
      title: "Name",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleViewUser(record)}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    const filtered = userList.filter((user) => {
      const name = user.name || "";
      const email = user.email || "";

      return (
        name.toLowerCase().includes(searchTerm) ||
        email.toLowerCase().includes(searchTerm)
      );
    });
    setFilteredUsers(filtered);
  };

  const showAddModal = () => {
    setIsAddModalOpen(true);
  };

  const showViewModal = () => {
    setIsViewModalOpen(true);
  };

  const handleCancelAddModal = () => {
    setIsAddModalOpen(false);
    form.resetFields();
  };

  const handleCancelViewModal = () => {
    setIsViewModalOpen(false);
    setViewedUser(null);
  };

  const handleAddUser = async (values) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/createUser`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(values),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await fetchUserList();
      setIsAddModalOpen(false);
      form.resetFields();
    } catch (error) {
      console.error("Error adding user:", error);
    }
  };

  const handleViewUser = (record) => {
    setViewedUser(record);
    setIsViewModalOpen(true);
  };

  return (
    <div>
      <Typography.Title level={3}>User List</Typography.Title>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={11} lg={11}>
            {userCountsByMonth.length > 0 && (
              <LineChart
                data={userCountsByMonth}
                xField="month"
                yField="users"
                title="Number Of Users"
                height={350}
                width={550}
              />
            )}
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} style={{ paddingLeft: "20px" }}>
            {averageTimeByMonth.length > 0 && (
              <LineChart
                data={averageTimeByMonth}
                xField="month"
                yField="averageTime"
                title="Thời gian hoạt động trung bình (Theo giây)"
                height={350}
                width={550}
              />
            )}
          </Col>
        </Row>
        <Col span={24} style={{ paddingRight: "20px" }}>
          <LineChart
            data={trafficData}
            xField="month"
            yField="traffic"
            title="Lưu lượng truy cập (Theo tháng)"
            height={430}
            width={800}
          />
        </Col>

        <Row
          justify="space-between"
          align="middle"
          style={{ marginTop: "40px" }}
        >
          <Col span={15} align="left">
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={showAddModal}
            >
              Add User
            </Button>
          </Col>
          <Col span={9} align="right">
            <Input
              ref={searchInput}
              placeholder="Search user"
              onChange={handleSearch}
              style={{ width: 250 }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
          <Col span={24} justify="space-between" align="middle">
            <Col span={24}>
              <Table
                columns={columns}
                dataSource={filteredUsers}
                rowKey="id"
                pagination={{ pageSize: 5 }}
                scroll={{ x: "max-content" }}
              />
            </Col>
          </Col>
        </Row>
      </Space>

      {/* Add User Modal */}
      <Modal
        title="Add New User"
        open={isAddModalOpen}
        onCancel={handleCancelAddModal}
        footer={null}
      >
        <Form form={form} onFinish={handleAddUser} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please input name" }]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please input email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* View User Modal */}
      <Modal
        title={
          viewedUser ? `User Details: ${viewedUser.username}` : "User Details"
        }
        open={isViewModalOpen}
        onCancel={handleCancelViewModal}
        footer={null}
      >
        {viewedUser && (
          <div>
            <Row>
              <Col span={10}>
                <img
                  src={viewedUser.avatar}
                  alt={viewedUser.username}
                  style={{ width: "100%", height: "auto" }}
                />
              </Col>
              <Col span={14} style={{ paddingLeft: "10px" }}>
                <p>
                  <strong>Name:</strong> {viewedUser.username}
                </p>
                <p>
                  <strong>Email:</strong> {viewedUser.email}
                </p>
                <p>
                  <strong>Role:</strong> {viewedUser.role}
                </p>
                <p>
                  <strong>Status:</strong> {viewedUser.status}
                </p>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default UserList;
